import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { adminApi } from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusTimeline from '../../components/common/StatusTimeline';
import NoteThread from '../../components/common/NoteThread';
import { format } from 'date-fns';
import { selectHasSuperAdminAccess, selectCurrentUser, selectUserRole } from '../../store/slices/authSlice';
import { filterByVendorName } from '../../utils/vendorNameMatch';

export function getBobsActivationStatus(value) {
  const status = typeof value === 'string' ? value.trim() : '';
  return status || 'NOT SET';
}

export default function FinishedGoodDetail() {
  const { id }        = useParams();
  const navigate      = useNavigate();
  const qc            = useQueryClient();
  const canAdminAssign = useSelector(selectHasSuperAdminAccess);
  const currentUser   = useSelector(selectCurrentUser);
  const userRole      = useSelector(selectUserRole);
  const [selected, setSelected] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [withdrawTarget, setWithdrawTarget] = useState(null); // { assignmentId, vendorName }
  const [cancelTarget, setCancelTarget]     = useState(null); // { assignmentId, vendorName }
  const [quickAssignConfirm, setQuickAssignConfirm] = useState(false);
  const { data: fgData, isLoading: fgLoading } = useQuery(['finished-good', id], () => adminApi.getFinishedGood(id));
  const { data: vendorData, isLoading: vendorsLoading } = useQuery('active-vendors', () =>
    adminApi.getAllUsers({ group: 'VENDOR', size: 100 })
  );

  const fg         = fgData?.data ?? fgData;
  const allVendors = vendorData?.data?.content ?? vendorData?.content ?? [];
  const vendors    = allVendors.filter(v => v.active !== false);
  // Elastic, typo-tolerant name search — matches "Twin Star"/"Twinstart" against
  // "TWIN STAR INTERNATIONAL", "Holland House" against either Holland House entry, etc.
  const filteredVendors = useMemo(
    () => filterByVendorName(vendors, vendorSearch, (v) => v.companyName),
    [vendors, vendorSearch]
  );

  const assignMutation = useMutation(adminApi.assignVendors, {
    onSuccess: () => {
      toast.success('Vendors assigned successfully!');
      qc.invalidateQueries(['finished-good', id]);
      qc.invalidateQueries('admin-stats');
      setSelected([]);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Assignment failed'),
  });

  const withdrawMutation = useMutation(adminApi.withdrawAssignment, {
    onSuccess: () => {
      toast.success('Assignment withdrawn');
      qc.invalidateQueries(['finished-good', id]);
      qc.invalidateQueries('admin-stats');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Withdraw failed'),
  });

  const cancelMutation = useMutation(({ assignmentId, reason }) => adminApi.cancelAssignment(assignmentId, reason), {
    onSuccess: () => {
      toast.success('Process cancelled');
      qc.invalidateQueries(['finished-good', id]);
      qc.invalidateQueries('finished-goods');
      qc.invalidateQueries('admin-stats');
      setCancelTarget(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Cancel failed'),
  });

  const quickAssignMutation = useMutation(() => adminApi.quickAssign(id), {
    onSuccess: () => {
      toast.success('Vendor quick-assigned successfully!');
      qc.invalidateQueries(['finished-good', id]);
      qc.invalidateQueries('admin-stats');
      setQuickAssignConfirm(false);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Quick Assign failed');
      setQuickAssignConfirm(false);
    },
  });

  // Single-vendor selection — clicking an already-selected vendor deselects it
  const selectVendor = (vid) => setSelected(p => p[0] === vid ? [] : [vid]);

  const handleAssign = () => {
    if (!selected.length) return toast.error('Please select a vendor');
    assignMutation.mutate({ finishedGoodId: Number(id), vendorIds: selected });
  };

  const handleWithdraw = (assignmentId, vendorName) => {
    setWithdrawTarget({ assignmentId, vendorName });
  };

  const confirmWithdraw = () => {
    if (withdrawTarget) {
      withdrawMutation.mutate(withdrawTarget.assignmentId);
      setWithdrawTarget(null);
    }
  };

  if (fgLoading) return <LoadingSpinner />;

  if (!fg) return <div className="text-center py-16 text-gray-400">Finished Good not found</div>;

  const assignedVendorIds = fg.assignments?.map(a => a.vendorId) || [];
  const canViewLifecycleFields = userRole?.startsWith('PLATFORM_') || userRole === 'CLIENT_VENDOR_ADMIN';
  const bobsActivationStatus = getBobsActivationStatus(fg.bobsActivation);
  const bobsActivationKey = bobsActivationStatus.toUpperCase();
  // Show "Assign Vendors" only when the FG has never been assigned yet
  const canAssign = fg.status === 'NEW';

  return (
    <div className="w-full pb-10 space-y-6">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div>
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-700 mb-2 flex items-center gap-1">
          ← Back
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{fg.fgName}</h1>
            <p className="text-gray-400 text-sm font-mono mt-0.5">{fg.fgNumber}</p>
          </div>
          <StatusBadge status={fg.status} />
        </div>
      </div>

      {/* ── Finished Goods Catalog ──────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-semibold text-gray-900">Finished Goods</h2>
          {canViewLifecycleFields && (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              bobsActivationKey === 'ACTIVE'
                ? 'bg-green-100 text-green-700'
                : bobsActivationKey === 'DROPPED (WILL DEACTIVATE)' || bobsActivationKey === 'INACTIVE'
                  ? 'bg-red-100 text-red-700'
                  : bobsActivationKey === 'SAMPLE'
                    ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-500'
            }`} title="BOBs Activation">
              <span className={`w-1.5 h-1.5 rounded-full ${
                bobsActivationKey === 'ACTIVE'
                  ? 'bg-green-500'
                  : bobsActivationKey === 'DROPPED (WILL DEACTIVATE)' || bobsActivationKey === 'INACTIVE'
                    ? 'bg-red-500'
                    : bobsActivationKey === 'SAMPLE'
                      ? 'bg-blue-500'
                    : 'bg-gray-400'
              }`} />
              {bobsActivationStatus}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-5">
          {[
            ['Product Title',              fg.productTitle],
            ['Finished Good Code',         fg.bobFinishedGoodCode],
            ['Country of Origin',          fg.countryOfOrigin],
            ['Top Category',               fg.topCategory],
            ['Category',                   fg.category],
            ['Collection Name',            fg.collectionName],
            ['Finished Product',           fg.finishedProduct],
            ['Vendor Finished Good Model', fg.vendorFinishedGoodModel],
            ['Factory Code',               fg.factoryCode],
            ['Factory Name',               fg.factoryName],
            ['Finish',                     fg.finish],
            ['Color',                      fg.color],
            ['Vendor Product Code',        fg.vendorProductCode],
            ['Package Sales Set',          fg.packageSalesSet],
            ['Package Sales Set Name',     fg.packageSalesSetName],
            ['Package Component Qty',      fg.packageComponentQty],
            ['Vendor Code (internal to Bobs)', fg.vendorCode],
            ['Vendor Name',                fg.vendorName],
            ...(canViewLifecycleFields ? [
              ['BOBs Activation',          bobsActivationStatus],
              ['Product Drop Code',        fg.productDropCode],
              ['Product Drop Date',        fg.productDropDate],
            ] : []),
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs text-gray-500 uppercase tracking-wide">{label}</dt>
              <dd className={`mt-0.5 text-sm font-medium ${
                label === 'Vendor Code (internal to Bobs)' && value
                  ? 'inline-block rounded bg-orange-50 py-0.5 pr-2 text-orange-600'
                  : 'text-gray-900'
              }`}>{value ?? '—'}</dd>
            </div>
          ))}
        </div>
        {fg.productDescription && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <dt className="text-xs text-gray-500 uppercase tracking-wide mb-1">Product Description</dt>
            <dd className="rounded-lg bg-gray-50 py-3 pr-3 text-sm text-gray-700">{fg.productDescription}</dd>
          </div>
        )}
      </div>

      {/* ── Request Details ─────────────────────────────────────────────────── */}
      <div className="bg-blue-50 rounded-xl border border-blue-100 p-6">
        <h2 className="font-semibold text-gray-900 mb-4">Request Details</h2>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            ['Quantity',      `${fg.quantity ?? '—'} ${fg.unitOfMeasure ?? ''}`],
            ['Account',       fg.clientName],
            ['Synced from SF', fg.salesforceSyncAt ? format(new Date(fg.salesforceSyncAt), 'dd MMM yyyy HH:mm') : '—'],
            ['Created',       fg.createdAt ? format(new Date(fg.createdAt), 'dd MMM yyyy') : '—'],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs text-gray-500 uppercase tracking-wide">{k}</dt>
              <dd className="mt-0.5 text-sm font-medium text-gray-900">{v || '—'}</dd>
            </div>
          ))}
        </dl>
        {fg.description && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <dt className="text-xs text-gray-500 uppercase tracking-wide mb-1">Description</dt>
            <dd className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{fg.description}</dd>
          </div>
        )}
      </div>

      {/* ── Suggested Vendor (Salesforce Quick Assign) ──────────────────────── */}
      {/* Show whenever Salesforce gave us a code OR a name to resolve — a name-only
          match (no vendor_code on the FG) is still a valid Quick Assign candidate
          since WorkflowService now falls back to fuzzy vendor-name matching. */}
      {(fg.suggestedVendor?.sfVendorCode || fg.suggestedVendor?.sfVendorName) && (
        <div className={`rounded-xl border p-5 ${
          fg.suggestedVendor.canQuickAssign
            ? 'bg-indigo-50 border-indigo-200'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2 flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                  Salesforce Suggested Vendor
                </span>
                {fg.suggestedVendor.canQuickAssign && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                    Ready to assign
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-2">
                {fg.suggestedVendor.sfVendorCode && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Vendor Code</p>
                    <p className="mt-0.5 inline-block rounded bg-orange-50 py-0.5 pr-2 text-sm font-semibold font-mono text-orange-600">
                      {fg.suggestedVendor.sfVendorCode}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Vendor Name</p>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">{fg.suggestedVendor.sfVendorName || '—'}</p>
                </div>
                {fg.suggestedVendor.matchedCompanyName && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1">
                      Matched Company
                      {fg.suggestedVendor.matchedBy === 'NAME' && (
                        <span
                          className="normal-case font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 text-[10px] tracking-normal"
                          title="No exact vendor code matched — resolved by matching the vendor name instead. Double-check before assigning."
                        >
                          matched by name
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-sm font-medium text-gray-900">{fg.suggestedVendor.matchedCompanyName}</p>
                    {fg.suggestedVendor.matchedCompanyEmail && (
                      <p className="text-xs text-gray-400">{fg.suggestedVendor.matchedCompanyEmail}</p>
                    )}
                  </div>
                )}
              </div>
              {!fg.suggestedVendor.canQuickAssign && fg.suggestedVendor.quickAssignBlockReason && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
                  <svg className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  {fg.suggestedVendor.quickAssignBlockReason}
                </p>
              )}
            </div>

            {/* Quick Assign button — PLATFORM_SUPER_ADMIN and CLIENT_VENDOR_ADMIN */}
            {canAdminAssign && canAssign && (
              <div className="flex-shrink-0">
                <button
                  onClick={() => setQuickAssignConfirm(true)}
                  disabled={!fg.suggestedVendor.canQuickAssign || quickAssignMutation.isLoading}
                  title={!fg.suggestedVendor.canQuickAssign ? fg.suggestedVendor.quickAssignBlockReason : 'Assign this vendor instantly'}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    fg.suggestedVendor.canQuickAssign
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Quick Assign
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Quick Assign Confirmation ────────────────────────────────────────── */}
      {quickAssignConfirm && (
        <ConfirmDialog
          title="Quick Assign Vendor"
          message={`This will instantly assign "${fg.suggestedVendor?.matchedCompanyName || fg.suggestedVendor?.sfVendorName}" to this Finished Good. The vendor admin will be notified. Continue?`}
          confirmLabel="Quick Assign"
          confirmClass="bg-indigo-600 hover:bg-indigo-700 text-white"
          isLoading={quickAssignMutation.isLoading}
          onConfirm={() => quickAssignMutation.mutate()}
          onCancel={() => setQuickAssignConfirm(false)}
        />
      )}

      {/* ── Assign Vendors (only when actionable) ───────────────────────────── */}
      {/* {canAssign && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-gray-900 mb-1">Assign Vendor</h2>
              <p className="text-xs text-gray-400 mb-4">Select one vendor to assign this request</p>
              {vendorsLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
                  <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Loading vendors…
                </div>
              ) : (
              <>
                <div className="relative mb-3">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
                  </svg>
                  <input
                    type="text"
                    value={vendorSearch}
                    onChange={(e) => setVendorSearch(e.target.value)}
                    placeholder="Search vendors by name…"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  />
                </div>
                {filteredVendors.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4">No vendors match "{vendorSearch}"</p>
                ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {filteredVendors.map((v) => {
                    const isSelected = selected[0] === v.id;
                    return (
                      <label key={v.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                        isSelected ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}>
                        <input
                          type="radio"
                          name="vendor-select"
                          checked={isSelected}
                          onChange={() => selectVendor(v.id)}
                          className="flex-shrink-0 accent-blue-600"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{v.fullName}</p>
                          <p className="text-xs text-gray-400 truncate">{v.companyName}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                )}
              </>
              )}
            </div>
            <div className="flex-shrink-0 pt-7">
              <button onClick={handleAssign} disabled={!selected.length || assignMutation.isLoading}
                className="px-6 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap">
                {assignMutation.isLoading ? 'Assigning...' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )} */}

      {/* ── Withdraw Confirmation Dialog ────────────────────────────────────── */}
      {withdrawTarget && (
        <ConfirmDialog
          title="Withdraw Assignment"
          message={`Are you sure you want to withdraw the assignment for ${withdrawTarget.vendorName || 'this vendor'}? They will be notified and the request will return to an assignable state.`}
          confirmLabel="Withdraw"
          confirmClass="bg-red-600 hover:bg-red-700 text-white"
          isLoading={withdrawMutation.isLoading}
          onConfirm={confirmWithdraw}
          onCancel={() => setWithdrawTarget(null)}
        />
      )}

      {/* ── Cancel Process Confirmation Dialog ─────────────────────────────── */}
      {cancelTarget && (
        <ConfirmDialog
          title="Cancel Process"
          message={`This will permanently cancel the process for ${cancelTarget.vendorName || 'this vendor'}. All parties will be notified and no further action can be taken. This cannot be undone.`}
          confirmLabel="Cancel Process"
          confirmClass="bg-gray-700 hover:bg-gray-800 text-white"
          isLoading={cancelMutation.isLoading}
          onConfirm={() => cancelMutation.mutate({ assignmentId: cancelTarget.assignmentId })}
          onCancel={() => setCancelTarget(null)}
        />
      )}

      {/* ── Assigned Vendors + Timelines ────────────────────────────────────── */}
      {fg.assignments?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">Assigned Vendors</h2>
          <div className="space-y-4">
            {fg.assignments.map((a) => (
              <div key={a.assignmentId} className="border border-gray-100 rounded-xl overflow-hidden">
                {/* Vendor header row */}
                <div className="flex items-center justify-between p-3 bg-gray-50">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{a.vendorName}</p>
                    {a.vendorEmail && <p className="text-xs text-gray-400 truncate">{a.vendorEmail}</p>}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <StatusBadge status={a.status} />
                    {['VENDOR_SUBMITTED', 'COMPANY_APPROVED', 'COMPANY_REJECTED', 'PENDING_FINAL_APPROVAL', 'CLIENT_APPROVED', 'CLIENT_REJECTED', 'CVA_REJECTED', 'CANCELLED'].includes(a.status) && (
                      <button
                        onClick={() => navigate(`/platform/assignments/${a.assignmentId}`)}
                        className="text-xs font-medium text-green-800 border border-green-700 hover:bg-green-50 px-2.5 py-1 rounded-md transition-colors"
                      >
                        View BOM →
                      </button>
                    )}
                    {canAdminAssign && a.status === 'ASSIGNED' && !a.isDraft && (
                      <button
                        onClick={() => handleWithdraw(a.assignmentId, a.vendorName)}
                        disabled={withdrawMutation.isLoading}
                        className="text-xs font-medium text-red-600 hover:text-red-800 px-2.5 py-1 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Withdraw
                      </button>
                    )}
                    {canAdminAssign && !['CANCELLED','CLIENT_APPROVED','PENDING_FINAL_APPROVAL'].includes(a.status) && (
                      <button
                        onClick={() => setCancelTarget({ assignmentId: a.assignmentId, vendorName: a.vendorName })}
                        disabled={cancelMutation.isLoading}
                        className="text-xs font-medium text-red-600 hover:text-red-800 px-2.5 py-1 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Cancel Process
                      </button>
                    )}
                  </div>
                </div>
                {/* Lifecycle timeline */}
                <div className="p-3 bg-white">
                  <StatusTimeline
                    fgCreatedAt={fg.createdAt}
                    assignedAt={a.assignedAt}
                    submittedAt={a.submittedAt}
                    reviewedAt={a.reviewedAt}
                    fgClientApprovedAt={fg.clientApprovedAt}
                    status={a.status}
                    companyNotes={a.companyNotes}
                    vendorName={a.vendorName}
                    vendorCompanyName={a.vendorCompanyName}
                    assignmentId={a.assignmentId}
                    fetchHistory={adminApi.getAssignmentHistory}
                    companyLabel="OmniDrive"
                    showRawPerformer={true}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Notes — after finished good details, shown when there are assignments ── */}
      {fg.assignments?.length > 0 && (() => {
        const activeAsgn = fg.assignments.find(a =>
          ['ASSIGNED','VENDOR_SUBMITTED','COMPANY_APPROVED','COMPANY_REJECTED','PENDING_FINAL_APPROVAL','CLIENT_APPROVED','CLIENT_REJECTED','CVA_REJECTED','CANCELLED'].includes(a.status)
        ) || fg.assignments[0];
        return (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              Notes
              <span className="text-xs text-gray-400 font-normal">— visible to OmniDrive, vendor &amp; account</span>
            </h3>
            <NoteThread
              assignmentId={activeAsgn.assignmentId}
              fetchNotes={adminApi.getAssignmentNotes}
              postNote={adminApi.addAssignmentNote}
              updateNote={adminApi.updateAssignmentNote}
              deleteNote={adminApi.deleteAssignmentNote}
              canPost={activeAsgn.status !== 'CANCELLED'}
              canModerate={canAdminAssign}
              currentUserId={currentUser?.userId}
              queryKey={`fg-notes-${activeAsgn.assignmentId}`}
            />
          </div>
        );
      })()}
    </div>
  );
}

// ── Reusable confirmation dialog ─────────────────────────────────────────────

function ConfirmDialog({ title, message, confirmLabel = 'Confirm', confirmClass = 'bg-blue-600 hover:bg-blue-700 text-white', isLoading, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95">
        {/* Icon + Title */}
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5 text-red-600">
              <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1 leading-relaxed">{message}</p>
          </div>
        </div>
        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 transition-colors ${confirmClass}`}>
            {isLoading ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
